import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  format,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { Download, X, Calendar, BookOpen, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { posApi, authApi } from '@/services/api';
import { getLocalCustomerPartyLedger, getLocalSupplierPartyLedger, isLocalFirstMode } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { formatMoney, getActiveCurrency } from '@/utils/currency';
import { dateRangeLabelKey } from '@/utils/dateRangeI18n';
import { REPORT_DATE_RANGE_OPTIONS } from '@/hooks/useReportDateRange';

export type PartyLedgerType = 'POS Customer' | 'POS Supplier';

interface PartyLedgerModalProps {
  isOpen: boolean;
  onClose?: () => void;
  partyType: PartyLedgerType;
  party: string;
  partyName: string;
  /** modal = popup overlay (default); inline = embed in page without overlay */
  variant?: 'modal' | 'inline';
}

interface LedgerEntry {
  posting_date: string;
  invoice_no: string;
  payment_no: string;
  description: string;
  items_string?: string;
  items?: any[];
  debit: number;
  credit: number;
  balance: number;
}

interface LedgerResponse {
  party_name: string;
  opening_balance: number;
  closing_balance: number;
  balance_label: string;
  entries: LedgerEntry[];
  from_date: string;
  to_date: string;
}

const formatDate = (d?: string) => {
  if (!d) return '';
  try {
    // YYYY-MM-DD must be local calendar date (SD-UK-016 off-by-one)
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d).trim());
    if (m && String(d).trim().length === 10) {
      return format(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), 'd-MMM-yyyy');
    }
    return format(new Date(d), 'd-MMM-yyyy');
  } catch {
    return d;
  }
};

export const PartyLedgerModal: React.FC<PartyLedgerModalProps> = ({
  isOpen,
  onClose,
  partyType,
  party,
  partyName,
  variant = 'modal',
}) => {
  const isInline = variant === 'inline';
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState('This Month');
  const [customFrom, setCustomFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customTo, setCustomTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const { from_date, to_date } = useMemo(() => {
    const today = new Date();
    switch (dateRange) {
      case 'Today':
        return { from_date: format(today, 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
      case 'Yesterday':
        return {
          from_date: format(subDays(today, 1), 'yyyy-MM-dd'),
          to_date: format(subDays(today, 1), 'yyyy-MM-dd'),
        };
      case 'This Week':
        return {
          from_date: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'Last Week':
        return {
          from_date: format(startOfWeek(subDays(today, 7), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to_date: format(endOfWeek(subDays(today, 7), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        };
      case 'This Month':
        return {
          from_date: format(startOfMonth(today), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'Last Month': {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return {
          from_date: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          to_date: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };
      }
      case 'This Quarter':
        return {
          from_date: format(startOfQuarter(today), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'This Year':
        return {
          from_date: format(startOfYear(today), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'Custom Date':
        return { from_date: customFrom, to_date: customTo };
      default:
        return {
          from_date: format(startOfMonth(today), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
    }
  }, [dateRange, customFrom, customTo]);

  const posSyncMode = useSyncStore((s) => s.posSyncMode);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['partyLedger', partyType, party, from_date, to_date, posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode() && partyType === 'POS Customer') {
        return (await getLocalCustomerPartyLedger({
          customer: party,
          customerName: partyName,
          from_date,
          to_date,
        })) as LedgerResponse;
      }
      if (isLocalFirstMode() && partyType === 'POS Supplier') {
        return (await getLocalSupplierPartyLedger({
          supplier: party,
          supplierName: partyName,
          from_date,
          to_date,
        })) as LedgerResponse;
      }
      const res = await posApi.getPartyLedger({
        party_type: partyType,
        party,
        from_date,
        to_date,
      });
      return (res.data.message || {}) as LedgerResponse;
    },
    enabled: isOpen && Boolean(party),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  React.useEffect(() => {
    if (isOpen && party) {
      refetch();
    }
  }, [isOpen, party, partyType, from_date, to_date, refetch]);

  const title = partyType === 'POS Customer' ? t('partyLedger.customerTitle') : t('partyLedger.supplierTitle');
  const partyLabel = partyType === 'POS Customer' ? t('reports.customer') : t('reports.supplier');

  const downloadCSV = () => {
    if (!data) return;
    const lines = [
      'Date,Invoice #,Payment #,Description,Debit,Credit,Balance',
      `,,,Opening Balance,,,${data.opening_balance ?? 0}`,
      ...(data.entries || []).map((row) =>
        [
          formatDate(row.posting_date),
          row.invoice_no || '',
          row.payment_no || '',
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.debit || 0,
          row.credit || 0,
          row.balance ?? 0,
        ].join(',')
      ),
      `,,,Closing Balance,,,${data.closing_balance ?? 0}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute(
      'download',
      `${partyType === 'POS Customer' ? 'customer' : 'supplier'}_ledger_${party}_${from_date}_${to_date}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = async () => {
    if (!data) return;
    
    const [html2canvas, { jsPDF }] = await Promise.all([
      import('html2canvas').then(m => m.default),
      import('jspdf')
    ]);
    const autoTable = (await import('jspdf-autotable')).default;

    // Fetch profile for header
    let profile: any = {};
    try {
      const res = await authApi.getMyProfile();
      profile = res.data?.message || res.data || {};
    } catch (e) {
      console.error('Failed to fetch profile', e);
    }
    
    const companyName = profile.company_name || profile.company?.name || profile.company || 'Company Name';
    const companyAddress = profile.address || '';
    const companyPhone = profile.phone || profile.mobile || profile.contact_no || '';
    const logoHtml = profile.company_logo ? `<img src="${profile.company_logo}" style="max-height: 50px; margin-bottom: 8px;" />` : '';
    
    const durationText = `${t(dateRangeLabelKey(dateRange))} ${dateRange === 'Custom Date' ? `(${customFrom} to ${customTo})` : ''}`;

    const escapeHtml = (s: string) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const headerHtml = `
      <div id="ledger-header-root" style="width:1122px;padding:40px 40px 10px 40px;background:#fff;color:#333;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;box-sizing:border-box;">
        <div style="display:flex; justify-content:space-between; align-items:flex-start;">
          <div style="width:50%;">
            ${logoHtml}
            <div style="font-size:20px; font-weight:700; color:#111; margin-bottom:4px;">${escapeHtml(companyName)}</div>
            <div style="font-size:11px; color:#555; line-height:1.4;">
              ${companyAddress ? escapeHtml(companyAddress) + '<br>' : ''}
              ${companyPhone ? escapeHtml(companyPhone) : ''}
            </div>
            
            <div style="margin-top:25px;">
              <div style="font-size:10px; font-weight:700; color:#888; text-transform:uppercase; margin-bottom:6px;">${escapeHtml(partyLabel)} Ledger</div>
              <div style="font-size:14px; font-weight:600; color:#111; margin-bottom:4px;">${escapeHtml(data?.party_name || partyName || party)}</div>
            </div>
          </div>
          <div style="width:50%; display:flex; flex-direction:column; align-items:flex-end;">
            <div style="font-size:28px; font-weight:300; color:#111; letter-spacing:1px; margin-bottom:12px; text-transform:uppercase;">${escapeHtml(title)}</div>
            <table style="width:auto; font-size:11px; text-align:right;">
              <tr>
                <td style="color:#666; padding-bottom:4px; padding-right:24px;">Duration</td>
                <td style="font-weight:600; color:#111; padding-bottom:4px;">${escapeHtml(durationText)}</td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    `;

    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.width = '1122px';
    container.style.zIndex = '-9999';
    container.style.opacity = '0';
    container.style.pointerEvents = 'none';
    container.innerHTML = headerHtml;
    document.body.appendChild(container);

    let imgData = null;
    let headerHeightPx = 0;
    try {
      const root = container.querySelector('#ledger-header-root') as HTMLElement;
      const canvas = await html2canvas(root, { scale: 2, useCORS: true, width: 1122 });
      imgData = canvas.toDataURL('image/png');
      headerHeightPx = canvas.height / 2;
    } finally {
      document.body.removeChild(container);
    }
    
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    
    let currentY = 10;
    if (imgData) {
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = (headerHeightPx * pdfWidth) / 1122;
      doc.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      currentY = pdfHeight;
    }
    
    const headers = [t('common.date'), t('partyLedger.invoiceNo'), t('partyLedger.paymentNo'), t('partyLedger.description'), t('partyLedger.debit'), t('partyLedger.credit'), t('reports.balance')];
      
    const body: string[][] = [];
    
    const activeCurrency = getActiveCurrency();
    const safeFormatMoney = (val: number) => `${activeCurrency} ${formatMoney(val, { locale: 'en-US', showSymbol: false })}`;
    
    body.push(['', '', '', t('reports.openingBalance'), '', '', safeFormatMoney(data.opening_balance ?? 0)]);

    (data.entries || []).forEach((row) => {
      body.push([
        formatDate(row.posting_date),
        row.invoice_no || '',
        row.payment_no || '',
        row.items_string ? `${row.description || ''}\n${row.items_string}`.trim() : (row.description || ''),
        row.debit > 0 ? safeFormatMoney(row.debit) : '',
        row.credit > 0 ? safeFormatMoney(row.credit) : '',
        safeFormatMoney(row.balance ?? 0)
      ]);
    });

    body.push(['', '', '', t('partyLedger.closingBalance'), '', '', safeFormatMoney(data.closing_balance ?? 0)]);

    autoTable(doc, {
      startY: currentY,
      head: [headers],
      body: body,
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 3, textColor: [51, 51, 51], lineColor: [229, 231, 235], lineWidth: { bottom: 0.1 } },
      headStyles: { textColor: [17, 17, 17], fontStyle: 'bold', lineWidth: { bottom: 0.5 } },
    });

    // Removing the extra Items Purchased table since items are now inline in the description

    doc.save(`${partyType === 'POS Customer' ? 'customer' : 'supplier'}_ledger_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
  };

  if (!isOpen) return null;

  const entries = data?.entries || [];
  const closing = data?.closing_balance ?? 0;

  const shellClass = isInline
    ? 'bg-white border border-gray-200 rounded-xl shadow-sm w-full flex flex-col overflow-hidden'
    : 'bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden';

  const content = (
      <div className={shellClass}>
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary-600" />
              {title}
            </h2>
            <p className="text-sm text-gray-500 mt-1 truncate">
              {t('partyLedger.runningBalance', { name: data?.party_name || partyName || party })}
            </p>
          </div>
          {!isInline && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50 space-y-4 shrink-0">
          <div className="flex flex-col sm:flex-wrap sm:flex-row gap-3 sm:gap-4 sm:items-end">
            <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[200px]">
              <label className="text-sm font-medium text-gray-700">{partyLabel}</label>
              <input
                className="input-field bg-gray-100 text-gray-700 cursor-not-allowed"
                value={data?.party_name || partyName || party}
                readOnly
              />
            </div>

            <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[180px]">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {t('dates.duration')}
              </label>
              <select
                className="input-field"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                {REPORT_DATE_RANGE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {t(dateRangeLabelKey(opt))}
                  </option>
                ))}
              </select>
            </div>

            {dateRange === 'Custom Date' && (
              <>
                <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[140px]">
                  <label className="text-sm font-medium text-gray-700">{t('dates.fromDate')}</label>
                  <input
                    type="date"
                    className="input-field"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[140px]">
                  <label className="text-sm font-medium text-gray-700">{t('dates.toDate')}</label>
                  <input
                    type="date"
                    className="input-field"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="flex gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={downloadCSV}
                disabled={!data}
                className="btn-primary w-full sm:w-auto py-2.5 px-4 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {t('reports.exportCsv')}
              </button>
              <button
                type="button"
                onClick={downloadPDF}
                disabled={!data}
                className="btn-primary w-full sm:w-auto py-2.5 px-4 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                Detailed PDF
              </button>
            </div>
          </div>

          {data && (
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="px-3 py-2 rounded-lg bg-white border border-gray-200">
                <span className="text-gray-500">{t('partyLedger.opening')} </span>
                <span className="font-semibold text-gray-900">{formatMoney(data.opening_balance)}</span>
              </div>
              <div className="px-3 py-2 rounded-lg bg-white border border-gray-200">
                <span className="text-gray-500">{t('partyLedger.closing')} </span>
                <span
                  className={`font-semibold ${
                    closing > 0 ? 'text-amber-700' : closing < 0 ? 'text-emerald-700' : 'text-gray-900'
                  }`}
                >
                  {formatMoney(closing)}
                </span>
                {data.balance_label && (
                  <span className="text-gray-500 ml-2">({data.balance_label})</span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`overflow-auto bg-white ${isInline ? 'max-h-[70vh]' : 'flex-1'}`}>
          {isLoading || (isFetching && !data) ? (
            <div className="p-8 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 bg-gray-50 uppercase sticky top-0 border-b border-gray-100 shadow-sm">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('common.date')}</th>
                  <th className="px-4 py-3 font-medium">{t('partyLedger.invoiceNo')}</th>
                  <th className="px-4 py-3 font-medium">{t('partyLedger.paymentNo')}</th>
                  <th className="px-4 py-3 font-medium">{t('partyLedger.description')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('partyLedger.debit')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('partyLedger.credit')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('reports.balance')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="bg-gray-50/80">
                  <td className="px-4 py-3 text-gray-400" colSpan={4}>
                    {t('reports.openingBalance')}
                  </td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    {formatMoney(data?.opening_balance ?? 0)}
                  </td>
                </tr>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                      {t('partyLedger.empty')}
                    </td>
                  </tr>
                ) : (
                  entries.map((row, idx) => {
                    const hasItems = row.items && row.items.length > 0;
                    const isExpanded = expandedRow === idx;
                    return (
                      <React.Fragment key={idx}>
                        <tr
                          className={`hover:bg-gray-50/50 ${hasItems ? 'cursor-pointer' : ''}`}
                          onClick={() => hasItems && setExpandedRow(isExpanded ? null : idx)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-gray-500 flex items-center gap-2">
                            {hasItems && (
                              <div className="text-gray-400">
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </div>
                            )}
                            {!hasItems && <div className="w-4 h-4" />}
                            {formatDate(row.posting_date)}
                          </td>
                      <td className="px-4 py-3 font-medium text-primary-600">{row.invoice_no || '—'}</td>
                      <td className="px-4 py-3 font-medium text-indigo-600">{row.payment_no || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs whitespace-pre-line" title={row.description}>
                        {row.description || '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {row.debit > 0 ? formatMoney(row.debit) : ''}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-700">
                        {row.credit > 0 ? formatMoney(row.credit) : ''}
                      </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">
                            {formatMoney(row.balance)}
                          </td>
                        </tr>
                        {isExpanded && hasItems && (
                          <tr>
                            <td colSpan={7} className="p-0 border-b border-gray-100">
                              <div className="bg-gray-50/50 p-4 border-l-4 border-primary-500">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Items</h4>
                                <div className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm">
                                  <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50/80 text-gray-500 text-xs uppercase tracking-wider">
                                      <tr>
                                        <th className="px-4 py-2 font-medium">Item</th>
                                        <th className="px-4 py-2 font-medium text-right">Qty</th>
                                        <th className="px-4 py-2 font-medium text-right">Rate</th>
                                        <th className="px-4 py-2 font-medium text-right">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-gray-700">
                                      {row.items!.map((item, itemIdx) => (
                                        <tr key={itemIdx} className="hover:bg-gray-50/50">
                                          <td className="px-4 py-2.5 font-medium text-gray-900">
                                            {item.item_name}
                                          </td>
                                          <td className="px-4 py-2.5 text-right">{item.quantity}</td>
                                          <td className="px-4 py-2.5 text-right">{formatMoney(Number(item.rate))}</td>
                                          <td className="px-4 py-2.5 text-right font-medium text-gray-900">{formatMoney(Number(item.amount))}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
                <tr className="bg-gray-50/80 border-t border-gray-200">
                  <td className="px-4 py-3 font-medium text-gray-700" colSpan={4}>
                    {t('partyLedger.closingBalance')}
                  </td>
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3" />
                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                    {formatMoney(closing)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
  );

  if (isInline) return content;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-gray-900/50 backdrop-blur-sm">
      {content}
    </div>
  );
};

export default PartyLedgerModal;
